import { Model, type ModelProps } from '@/infra/model'
import { collection } from '@/infra/collection'
import { action, computed, observable } from 'mobx'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import type { BookkeepingStage } from './bookkeeping-stage'
import type { BookkeepingAccessStatus } from './bookkeeping-access-status'
import type { BookkeepingSoftware } from './bookkeeping-software'

/**
 * Props for {@link BookkeepingProgressionInfo}.
 */
export interface BookkeepingProgressionInfoProps extends ModelProps<BookkeepingProgressionInfo> {
  workItems?: WorkItemProps[]
}

export class BookkeepingProgressionInfo extends Model<BookkeepingProgressionInfo> {
  // Work items keep their identity across updates so their editors survive
  // realtime refreshes.
  private readonly _workItems = collection({
    create: () => new WorkItem(),
    getModelId: (m) => m.workItemId,
    getDataId: (d) => d.workItemId,
  })

  @observable accessor id = Id.none
  @observable accessor stage: BookkeepingStage = 'BookkeepingStageUnspecified'
  @observable accessor accessStatus: BookkeepingAccessStatus =
    'AccessStatusUnspecified'

  @observable accessor cycles: BookkeepingCycle[] = []
  @observable accessor platformConnection: ProvidedPlatformConnection | null =
    null

  @observable accessor bookkeepingSoftware: BookkeepingSoftware | null = null

  /**
   * The work items.
   */
  @computed
  get workItems(): readonly WorkItem[] {
    return this._workItems.items
  }

  /**
   * The cycle in progress, if any.
   */
  @computed
  get currentCycle() {
    return this.cycles.find((c) => c.status.type === 'InProgress')
  }

  @action.bound
  override set({
    workItems,
    ...props
  }: Partial<BookkeepingProgressionInfoProps>): this {
    if (workItems) {
      this._workItems.reset(workItems)
    }

    return super.set(props)
  }
}

/**
 * Work item completion.
 */
export interface WorkItemCompletion {
  id: Id
  completedCount: number
  name: string
  description: string
}

export type WorkItemProps = ModelProps<WorkItem>

/**
 * Work item.
 */
export class WorkItem extends Model<WorkItem> {
  @observable accessor workItemId = Id.none
  @observable accessor inventoryItemId = Id.none
  @observable accessor inventoryItemName = ''
  @observable accessor inventoryItemDescription = ''
  @observable accessor quantity = 0
  @observable accessor completedCount = 0

  /**
   * The number of items yet to be completed.
   */
  @computed
  get remaining() {
    return this.quantity - this.completedCount
  }
}

/**
 * Bookkeeping cycle.
 */
export interface BookkeepingCycle {
  id: Id
  number: number
  startedAt: Date
  status: BookkeepingCycleStatus
  correction: BookkeepingCycleCorrection | null
}

export const BookkeepingCycle = {
  /**
   * Empty bookkeeping cycle.
   */
  empty: {
    id: Id.none,
    number: 0,
    startedAt: unixEpoch(),
    status: { type: 'Unknown' },
    correction: null,
  } satisfies BookkeepingCycle,
}

/**
 * The date the cycle was completed, if it was.
 */
export function getBookkeepingCycleCompletedAt(cycle: BookkeepingCycle) {
  return cycle.status.type === 'Completed' ? cycle.status.completedAt : null
}

/**
 * The completed work items of the cycle, as corrected if it was.
 */
export function getBookkeepingCycleWorkItemsCompletion(
  cycle: BookkeepingCycle,
): WorkItemCompletion[] {
  if (cycle.correction) {
    return cycle.correction.items
  }

  return cycle.status.type === 'Completed' ? cycle.status.items : []
}

/**
 * The bookkeeping cycle status.
 */
export type BookkeepingCycleStatus =
  | BookkeepingCycleInProgress
  | BookkeepingCycleCompleted
  | BookkeepingCycleCanceled
  | BookkeepingCycleUnknown

interface BookkeepingCycleInProgress {
  type: 'InProgress'
}

interface BookkeepingCycleCompleted {
  type: 'Completed'
  completedAt: Date
  completedById: Id
  items: WorkItemCompletion[]
}

interface BookkeepingCycleCanceled {
  type: 'Canceled'
  canceledAt: Date
}

interface BookkeepingCycleUnknown {
  type: 'Unknown'
}

/**
 * A correction of the items completed in a cycle.
 */
export interface BookkeepingCycleCorrection {
  correctedAt: Date
  correctedById: Id
  items: WorkItemCompletion[]
}

/**
 * The bookkeeping platform connection.
 */
export type ProvidedPlatformConnection =
  QuickBooksPlatformConnection | XeroPlatformConnection

interface QuickBooksPlatformConnection {
  type: 'QuickBooks'
  quickBooks: {
    connectionId: Id
    realmId: Id
    companyName: string
  }
}

interface XeroPlatformConnection {
  type: 'Xero'
  xero: {
    connectionId: Id
    tenantId: Id
    tenantName: string
  }
}
